// ─────────────────────────────────────────────────────────────
// Modèle de données commun à toutes les catégories d'équipement
//
//  equipment_categories/{id}
//    title, emoji, route, collection (ex: "calculator_slots"),
//    price, loanMinutes (null = pas de limite), order, active, couleurs…
//
//  <collection>/{slot_N}          (calculator_slots, powerbank_slots, headphone_slots…)
//    name, short, brand, model, image, order,
//    status: available | borrowed | return_pending | defective | maintenance
//    userId, userName, loanId, takenAt   (remplis seulement pendant un emprunt)
//    reportedDefective                    (signalé par le membre à la prise)
//
//  loans/{id}                      (UNE collection pour tous les emprunts)
//    slotCollection, categoryId, categoryTitle,
//    slotDocId, slotNumber, slotName,
//    userId, userName,
//    takenAt, dueAt, loanMinutes,
//    status: active | return_pending | returned | cancelled
//    condition (good|defective, déclaré par le membre),
//    returnDeclaredAt, returnedAt, returnedBy, returnCondition
// ─────────────────────────────────────────────────────────────
import {
  collection, doc, runTransaction, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

export const SLOT_STATUS = {
  AVAILABLE: "available",
  BORROWED: "borrowed",
  RETURN_PENDING: "return_pending",
  DEFECTIVE: "defective",
  MAINTENANCE: "maintenance",
};

export const LOAN_STATUS = {
  ACTIVE: "active",
  RETURN_PENDING: "return_pending",
  RETURNED: "returned",
  CANCELLED: "cancelled",
};

export const OPEN_LOAN_STATUSES = [LOAN_STATUS.ACTIVE, LOAN_STATUS.RETURN_PENDING];

export const SLOT_STATUS_META = {
  available:      { label: "Disponible",       color: "#22c55e", tag: "green" },
  borrowed:       { label: "Emprunté",         color: "#ef4444", tag: "red" },
  return_pending: { label: "Retour à valider", color: "#f59e0b", tag: "orange" },
  defective:      { label: "Défectueux",       color: "#94a3b8", tag: "volcano" },
  maintenance:    { label: "Maintenance",      color: "#94a3b8", tag: "default" },
};

export const LOAN_STATUS_META = {
  active:         { label: "En cours",         tag: "blue" },
  return_pending: { label: "Retour à valider", tag: "orange" },
  returned:       { label: "Rendu",            tag: "green" },
  cancelled:      { label: "Annulé",           tag: "default" },
};

// Statut d'un slot — gère aussi l'ancien format ESP32 (present/authorized/available)
export function slotStatus(slot) {
  if (!slot) return SLOT_STATUS.MAINTENANCE;
  if (slot.status) return slot.status;
  if (slot.defective) return SLOT_STATUS.DEFECTIVE;
  if (slot.userId || slot.available === false || slot.authorized || slot.present === false) {
    return SLOT_STATUS.BORROWED;
  }
  return SLOT_STATUS.AVAILABLE;
}

export const isSlotAvailable = (slot) => slotStatus(slot) === SLOT_STATUS.AVAILABLE;

export function getSlotNumber(slot) {
  if (!slot) return null;
  // Trailing number of the doc ID: pb_1, slot_1, calc-07, headphone_12…
  const m = String(slot.docId ?? "").match(/(\d+)$/);
  if (m) return parseInt(m[1], 10);
  if (typeof slot.id === "number") return slot.id;
  if (Number.isInteger(slot.order)) return slot.order;
  return null;
}

export const pad2 = (n) => (n === null || n === undefined ? "??" : String(n).padStart(2, "0"));

export function slotLabel(slot) {
  if (!slot) return "—";
  return slot.name
    || [slot.brand, slot.model].filter(Boolean).join(" ")
    || `Slot ${pad2(getSlotNumber(slot))}`;
}

export const slotImage = (slot) => slot?.image || slot?.img || null;

export function compareSlots(a, b) {
  const oa = a.order ?? getSlotNumber(a) ?? 9999;
  const ob = b.order ?? getSlotNumber(b) ?? 9999;
  if (oa !== ob) return oa - ob;
  return (a.docId || "").localeCompare(b.docId || "");
}

export function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v.seconds !== undefined) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export function formatCountdown(seconds) {
  if (!seconds || seconds <= 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function isLoanOverdue(loan, now = Date.now()) {
  if (!loan || loan.status !== LOAN_STATUS.ACTIVE) return false;
  const due = toDate(loan.dueAt);
  return !!due && now > due.getTime();
}

const EMPTY_SLOT_LOAN = { userId: null, userName: null, loanId: null, takenAt: null };

// ── Membre : emprunter (transaction → deux membres ne peuvent pas prendre le même slot)
export async function borrowSlot({ slotCollection, candidates, user, category, loanMinutes }) {
  const userName = user.displayName || user.email || "Inconnu";
  for (const cand of candidates) {
    const slotRef = doc(db, slotCollection, cand.docId);
    const loanRef = doc(collection(db, "loans"));
    const ok = await runTransaction(db, async (tx) => {
      const snap = await tx.get(slotRef);
      if (!snap.exists()) return false;
      const data = { docId: snap.id, ...snap.data() };
      if (!isSlotAvailable(data)) return false;

      const now = Timestamp.now();
      const dueAt = loanMinutes
        ? Timestamp.fromMillis(now.toMillis() + loanMinutes * 60_000)
        : null;

      tx.update(slotRef, {
        status: SLOT_STATUS.BORROWED,
        userId: user.uid, userName, loanId: loanRef.id, takenAt: now,
        reportedDefective: false,
      });
      tx.set(loanRef, {
        slotCollection,
        categoryId: category?.id ?? null,
        categoryTitle: category?.title ?? null,
        slotDocId: cand.docId,
        slotNumber: getSlotNumber(data),
        slotName: slotLabel(data),
        userId: user.uid, userName,
        takenAt: now, dueAt, loanMinutes: loanMinutes ?? null,
        status: LOAN_STATUS.ACTIVE,
        condition: null,
        returnDeclaredAt: null, returnedAt: null, returnedBy: null, returnCondition: null,
      });
      return true;
    });
    if (ok) return { loanId: loanRef.id, slotDocId: cand.docId };
  }
  return null;
}

// ── Membre : déclarer le retour (l'accueil doit valider)
export async function declareReturn(loan) {
  const loanRef = doc(db, "loans", loan.id);
  const slotRef = doc(db, loan.slotCollection, loan.slotDocId);
  await runTransaction(db, async (tx) => {
    const slotSnap = await tx.get(slotRef);
    tx.update(loanRef, { status: LOAN_STATUS.RETURN_PENDING, returnDeclaredAt: serverTimestamp() });
    if (slotSnap.exists() && slotSnap.data().loanId === loan.id) {
      tx.update(slotRef, { status: SLOT_STATUS.RETURN_PENDING });
    }
  });
}

// ── Admin : valider un retour (ou forcer un retour sur un emprunt actif)
export async function confirmReturn(loan, { condition = "good", by = null } = {}) {
  const loanRef = doc(db, "loans", loan.id);
  const slotRef = doc(db, loan.slotCollection, loan.slotDocId);
  await runTransaction(db, async (tx) => {
    const slotSnap = await tx.get(slotRef);
    tx.update(loanRef, {
      status: LOAN_STATUS.RETURNED,
      returnedAt: serverTimestamp(),
      returnedBy: by,
      returnCondition: condition,
    });
    const s = slotSnap.exists() ? slotSnap.data() : null;
    if (s && (!s.loanId || s.loanId === loan.id)) {
      tx.update(slotRef, {
        ...EMPTY_SLOT_LOAN,
        status: condition === "defective" ? SLOT_STATUS.DEFECTIVE : SLOT_STATUS.AVAILABLE,
        reportedDefective: condition === "defective",
        availableSince: serverTimestamp(), // sert à proposer la powerbank rechargée depuis le plus longtemps
      });
    }
  });
}

// ── Admin : le membre a déclaré un retour mais rien n'a été rendu
export async function rejectReturn(loan) {
  const loanRef = doc(db, "loans", loan.id);
  const slotRef = doc(db, loan.slotCollection, loan.slotDocId);
  await runTransaction(db, async (tx) => {
    const slotSnap = await tx.get(slotRef);
    tx.update(loanRef, { status: LOAN_STATUS.ACTIVE, returnDeclaredAt: null });
    if (slotSnap.exists() && slotSnap.data().loanId === loan.id) {
      tx.update(slotRef, { status: SLOT_STATUS.BORROWED });
    }
  });
}

// ── Admin : annuler un emprunt créé par erreur
export async function cancelLoan(loan, { by = null } = {}) {
  const loanRef = doc(db, "loans", loan.id);
  const slotRef = doc(db, loan.slotCollection, loan.slotDocId);
  await runTransaction(db, async (tx) => {
    const slotSnap = await tx.get(slotRef);
    tx.update(loanRef, { status: LOAN_STATUS.CANCELLED, returnedAt: serverTimestamp(), returnedBy: by });
    if (slotSnap.exists() && slotSnap.data().loanId === loan.id) {
      tx.update(slotRef, { ...EMPTY_SLOT_LOAN, status: SLOT_STATUS.AVAILABLE });
    }
  });
}

export const emptySlotLoanFields = EMPTY_SLOT_LOAN;
